// Mercapoli - Admin Users Management
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Users, Search, Loader2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { admin as adminApi } from '../../lib/api';
import { toast } from 'sonner';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [rolActivo, setRolActivo] = useState('all');

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const data = await adminApi.getUsuarios();
      setUsuarios(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const toggleEstado = async (userId) => {
    try {
      await adminApi.toggleUsuarioEstado(userId);
      toast.success('Estado actualizado');
      loadUsuarios();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const getRolBadge = (rol) => {
    const badges = {
      admin: 'bg-purple-100 text-purple-800',
      customer: 'bg-blue-100 text-blue-800',
      delivery: 'bg-green-100 text-green-800',
      restaurant: 'bg-orange-100 text-orange-800',
    };
    return badges[rol] || badges.customer;
  };

  const filteredUsuarios = usuarios.filter(u => {
    const matchRol = rolActivo === 'all' || u.rol === rolActivo;
    const matchFiltro = !filtro || 
      u.nombre?.toLowerCase().includes(filtro.toLowerCase()) ||
      u.email?.toLowerCase().includes(filtro.toLowerCase());
    return matchRol && matchFiltro;
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-lg hover:bg-muted">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="font-heading text-lg font-bold">Usuarios</h1>
            </div>
            <Badge variant="outline">{usuarios.length} usuarios</Badge>
          </div>
        </div>
      </header>

      <div className="container-app py-6">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o email..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Tabs by role */}
        <Tabs value={rolActivo} onValueChange={setRolActivo} className="mb-4">
          <TabsList className="grid grid-cols-5">
            <TabsTrigger value="all">Todos</TabsTrigger>
            <TabsTrigger value="customer">Clientes</TabsTrigger>
            <TabsTrigger value="delivery">Delivery</TabsTrigger>
            <TabsTrigger value="restaurant">Rest.</TabsTrigger>
            <TabsTrigger value="admin">Admin</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-spicy-coral" />
          </div>
        ) : filteredUsuarios.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No se encontraron usuarios</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsuarios.map((usuario) => (
              <Card key={usuario.user_id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <span className="font-medium">
                          {usuario.nombre?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{usuario.nombre}</p>
                        <p className="text-sm text-muted-foreground">{usuario.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getRolBadge(usuario.rol)}>
                        {usuario.rol}
                      </Badge>
                      <button
                        onClick={() => toggleEstado(usuario.user_id)}
                        className={`p-2 rounded-lg transition-colors ${
                          usuario.activo 
                            ? 'text-zesty-lime hover:bg-zesty-lime/10' 
                            : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {usuario.activo ? (
                          <ToggleRight className="h-6 w-6" />
                        ) : (
                          <ToggleLeft className="h-6 w-6" />
                        )}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
